import React, { useMemo, useState, useEffect } from "react";

const SIZE = 920;
const CENTER = SIZE / 2;
const NODE_COUNT = 140;
const SCALE = 31;
const GOLDEN_ANGLE = 137.50776 * (Math.PI / 180);

// Node layout follows phyllotaxis (the golden-angle spiral behind sunflower
// heads and spiral galaxies): angle = i * golden angle, radius = k * sqrt(i).
// It's a real piece of mathematics, not a random scatter, which is the point.
function buildGalaxy() {
  const nodes = [];
  for (let i = 0; i < NODE_COUNT; i++) {
    const angle = i * GOLDEN_ANGLE;
    const radius = SCALE * Math.sqrt(i);
    nodes.push({
      id: `n${i}`,
      cx: radius * Math.cos(angle),
      cy: radius * Math.sin(angle),
      radius,
      r: 1.1 + (i % 11 === 0 ? 2.6 : i % 5 === 0 ? 1.4 : 0.5),
    });
  }

  // Each node joins its two nearest neighbours: a simplicial 1-skeleton.
  const edgeSet = new Set();
  const edges = [];
  nodes.forEach((a, i) => {
    const nearest = nodes
      .map((b, j) => ({ j, d: i === j ? Infinity : Math.hypot(a.cx - b.cx, a.cy - b.cy) }))
      .sort((p, q) => p.d - q.d)
      .slice(0, 2);
    nearest.forEach(({ j, d }) => {
      if (d > SCALE * 2.6) return;
      const key = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (!edgeSet.has(key)) {
        edgeSet.add(key);
        edges.push([nodes[i], nodes[j]]);
      }
    });
  });

  // Where three nodes are mutually close, fill the 2-simplex (the triangular
  // face), which is what turns a graph into an actual simplicial complex.
  const adjacency = new Map();
  edgeSet.forEach((key) => {
    const [a, b] = key.split("-");
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    if (!adjacency.has(b)) adjacency.set(b, new Set());
    adjacency.get(a).add(b);
    adjacency.get(b).add(a);
  });
  const triangleSet = new Set();
  const triangles = [];
  adjacency.forEach((neighbours, a) => {
    const list = [...neighbours];
    for (let x = 0; x < list.length; x++) {
      for (let y = x + 1; y < list.length; y++) {
        const b = list[x];
        const c = list[y];
        if (adjacency.get(b)?.has(c)) {
          const key = [a, b, c].sort().join("-");
          if (!triangleSet.has(key)) {
            triangleSet.add(key);
            triangles.push([nodes[+a], nodes[+b], nodes[+c]]);
          }
        }
      }
    }
  });

  return { nodes, edges, triangles };
}

const Constellation = ({ className = "" }) => {
  const { nodes, edges, triangles } = useMemo(buildGalaxy, []);
  const [animate, setAnimate] = useState(true);
  const maxRadius = SCALE * Math.sqrt(NODE_COUNT);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setAnimate(!mq.matches);
  }, []);

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      fill="none"
      aria-hidden="true"
      className={`overflow-visible ${className}`}
    >
      <g transform={`translate(${CENTER} ${CENTER})`}>
        <g className={animate ? "constellation-spin" : ""}>
          <g className="text-accent" fill="currentColor">
            {triangles.map(([a, b, c], i) => (
              <polygon
                key={i}
                points={`${a.cx},${a.cy} ${b.cx},${b.cy} ${c.cx},${c.cy}`}
                opacity={0.09}
              />
            ))}
          </g>
          <g stroke="currentColor" className="text-line" strokeWidth="1">
            {edges.map(([a, b], i) => (
              <line
                key={i}
                x1={a.cx}
                y1={a.cy}
                x2={b.cx}
                y2={b.cy}
                opacity={0.6}
              />
            ))}
          </g>
          <g className="text-accent" fill="currentColor">
            {nodes.map((n, i) => (
              <circle
                key={n.id}
                className={animate ? "constellation-node" : ""}
                cx={n.cx}
                cy={n.cy}
                r={n.r}
                opacity={Math.max(0.2, 1 - n.radius / maxRadius)}
                style={animate ? { animationDelay: `${(i % 13) * 0.45}s` } : undefined}
              />
            ))}
          </g>
        </g>
      </g>
    </svg>
  );
};

export default Constellation;
